// Centro de contacto con el Puente XmarteK (D80): colas en vivo (entrar/salir), devoluciones automaticas y desvio.
// Se usa en Reportes -> Equipo hoy (todas las colas; el administrador conecta o desconecta a cualquiera) y en la isla
// del telefono (solo mis colas y mi desvio).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { accionPuente, colasPuente, ESTADO_DEVOLUCION, estadoPuente, horarioPuente, type ConfigDevoluciones, type Devolucion } from '../api/puente';
import { formatPhone } from '../lib/phone-number';
import { alRecibir } from '../lib/tiempoReal';
import { Pill } from './bits';
import { toast } from './toasts';

export const useEstadoPuente = () => useQuery({ queryKey: ['puente-estado'], queryFn: estadoPuente, staleTime: 60_000, retry: false });

async function hacer(accion: string, datos: Record<string, unknown>, ok: string) {
  try {
    await accionPuente(accion, datos);
    toast(ok);
    return true;
  } catch (e) {
    toast((e as Error).message, 'error');
    return false;
  }
}

/** Colas de la central: llamadas esperando y agentes conectados. soloMias: solo las colas donde estoy. */
export function ColasEnVivo({ soloMias = false }: { soloMias?: boolean }) {
  const est = useEstadoPuente();
  const qc = useQueryClient();
  const colas = useQuery({ queryKey: ['puente-colas'], queryFn: colasPuente, enabled: !!est.data?.registrado, refetchInterval: 15_000, retry: false });
  if (!est.data?.registrado) return null;
  const miExt = est.data.extension;
  const lista = (colas.data ?? []).filter((c) => !soloMias || c.agentes.some((a) => a.extension === miExt));
  const cambiar = async (cola: string, extension: string, conectado: boolean) => {
    if (await hacer('cola', { cola, extension, conectado }, conectado ? `Conectado a la cola ${cola}` : `Desconectado de la cola ${cola}`)) void qc.invalidateQueries({ queryKey: ['puente-colas'] });
  };
  if (colas.error) return <p className="note">No se pudieron leer las colas: {(colas.error as Error).message}</p>;
  if (!lista.length) return soloMias ? null : <p className="note">{colas.isLoading ? 'Leyendo las colas…' : 'Tu central no tiene colas.'}</p>;
  return (
    <div className="rows">
      {lista.map((c) => (
        <div key={c.extension} className="row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto', alignItems: 'start' }}>
          <span>
            <b>{c.nombre || `Cola ${c.extension}`}</b> <span className="sub">· {c.extension}</span>
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {c.agentes
                .filter((a) => !soloMias || a.extension === miExt)
                .map((a) => {
                  const puede = (est.data?.colas ?? est.data?.admin) || a.extension === miExt;
                  return (
                    <button
                      key={a.extension}
                      type="button"
                      className={`btn sm ${a.conectado ? 'primary' : ''}`}
                      disabled={!puede}
                      title={puede ? (a.conectado ? 'Clic para sacarlo de la cola' : 'Clic para conectarlo a la cola') : 'Solo un administrador cambia a otros agentes'}
                      onClick={() => void cambiar(c.extension, a.extension, !a.conectado)}
                    >
                      {a.nombre || a.extension} · {a.extension} {a.conectado ? '● en cola' : '○ fuera'}
                    </button>
                  );
                })}
            </span>
          </span>
          <Pill tone={c.esperando ? 'warn' : 'ok'} dot>
            {c.esperando ? `${c.esperando} esperando` : 'Nadie esperando'}
          </Pill>
        </div>
      ))}
    </div>
  );
}

/** Devoluciones automaticas (callback) de hoy, con su estado al instante. */
export function Devoluciones() {
  const est = useEstadoPuente();
  const [lista, setLista] = useState<Devolucion[]>([]);
  useEffect(() => setLista(est.data?.devoluciones ?? []), [est.data?.devoluciones]);
  useEffect(
    () =>
      alRecibir((e) => {
        if (e.tipo !== 'devolucion') return;
        const d = (e as unknown as { devolucion: Devolucion }).devolucion;
        setLista((l) => [d, ...l.filter((x) => x.id !== d.id)].slice(0, 30));
      }),
    [],
  );
  if (!est.data?.registrado || !lista.length) return null;
  return (
    <div className="rows">
      {lista.map((d) => (
        <div key={d.id} className="row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
          <span>
            <b>{formatPhone(d.numero)}</b>
            <span className="sub" style={{ display: 'block' }}>
              para {d.destino}
              {d.motivo ? ` · ${d.motivo}` : ''}
            </span>
          </span>
          <Pill tone={d.estado === 'transferred' ? 'ok' : d.estado === 'failed' || d.estado === 'no_answer' ? 'bad' : d.estado === 'programada' ? 'acc' : 'warn'} dot>
            {ESTADO_DEVOLUCION[d.estado] ?? d.estado}
          </Pill>
        </div>
      ))}
    </div>
  );
}

/** Mi desvio: cuando 3CX me timbre, el puente pasa la llamada a este numero (p. ej. mi celular). */
export function MiDesvio() {
  const est = useEstadoPuente();
  const qc = useQueryClient();
  const [numero, setNumero] = useState('');
  if (!est.data?.registrado || !est.data.extension) return null;
  const activo = est.data.desvio;
  const guardar = async (destino: string | null) => {
    if (await hacer('desvio', { destino }, destino ? `Tus llamadas se desvían a ${formatPhone(destino)}` : 'Desvío quitado')) {
      setNumero('');
      void qc.invalidateQueries({ queryKey: ['puente-estado'] });
    }
  };
  return activo ? (
    <p className="note" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      Tus llamadas de 3CX se desvían a <b>{formatPhone(activo)}</b>
      <button className="btn sm" type="button" onClick={() => void guardar(null)}>
        Quitar desvío
      </button>
    </p>
  ) : (
    <form
      className="note"
      style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (numero.trim()) void guardar(numero.trim());
      }}
    >
      Desviar mis llamadas a
      <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="Celular o extensión" style={{ width: 170 }} />
      <button className="btn sm primary" disabled={!numero.trim()}>
        Desviar
      </button>
    </form>
  );
}

const MODOS_HORARIO: { modo: string; nombre: string }[] = [
  { modo: 'normal', nombre: 'Horario normal' },
  { modo: 'abierto', nombre: 'Forzar abierto' },
  { modo: 'cerrado', nombre: 'Forzar cerrado' },
  { modo: 'pausa', nombre: 'Forzar pausa' },
  { modo: 'festivo', nombre: 'Forzar festivo' },
];

/** Administracion: horario de la central y devoluciones automaticas (Puente XmarteK). */
export function AdminCentroContacto() {
  const est = useEstadoPuente();
  const qc = useQueryClient();
  const horario = useQuery({ queryKey: ['puente-horario'], queryFn: horarioPuente, enabled: !!est.data?.registrado && !!est.data?.admin, retry: false });
  const [cambios, setCambios] = useState<Partial<ConfigDevoluciones>>({});
  if (!est.data?.registrado || !est.data.admin) return null;
  const cfg: ConfigDevoluciones = { ...est.data.config, ...cambios };
  const poner = (k: keyof ConfigDevoluciones, v: string | number | boolean) => setCambios((c) => ({ ...c, [k]: v }));
  const num = (k: keyof ConfigDevoluciones, etiqueta: string, sufijo: string, min: number, max: number) => (
    <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {etiqueta}
      <input type="number" min={min} max={max} value={Number(cfg[k])} onChange={(e) => poner(k, Number(e.target.value))} style={{ width: 70 }} />
      {sufijo}
    </label>
  );
  return (
    <section className="box">
      <h2 className="lbl">Centro de contacto · Puente XmarteK</h2>
      <h3 className="lbl" style={{ marginTop: 8 }}>
        Horario de la central
      </h3>
      <p className="note">Fuerza el horario de todos los departamentos hasta el final del día (p. ej. un cierre de emergencia). «Horario normal» vuelve al horario programado.</p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {MODOS_HORARIO.map((m) => (
          <button
            key={m.modo}
            type="button"
            className={`btn sm ${m.modo === 'cerrado' ? 'danger' : ''}`}
            onClick={() => void hacer('horario', { modo: m.modo }, `Central: ${m.nombre.toLowerCase()}`).then(() => qc.invalidateQueries({ queryKey: ['puente-horario'] }))}
          >
            {m.nombre}
          </button>
        ))}
      </div>
      {horario.data?.length ? (
        <p className="sub" style={{ marginTop: 6 }}>
          Ahora: {horario.data.map((d) => `${d.nombre}: ${d.modo}`).join(' · ')}
        </p>
      ) : null}

      <h3 className="lbl" style={{ marginTop: 16 }}>
        Devoluciones automáticas
      </h3>
      <p className="note">
        Cuando una llamada se pierde, el CRM la devuelve solo: espera el tiempo que elijas y, apenas la cola tenga un agente libre (o la extensión esté libre),
        llama al cliente y al contestar lo pasa a esa misma cola o extensión. Si no contesta, reintenta. Si el cliente vuelve a llamar y lo atienden, la
        devolución se cancela. Las llamadas internas no se devuelven.
      </p>
      <form
        style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}
        onSubmit={(e) => {
          e.preventDefault();
          void accionPuente<{ ok: boolean; contesta?: boolean }>('devoluciones-config', { ...cfg })
            .then((r) => {
              setCambios({});
              toast(
                !cfg.activo
                  ? 'Devoluciones automáticas apagadas'
                  : r.contesta
                    ? `Devoluciones activadas: el CRM contesta solo la extensión ${cfg.extensionDevoluciones}`
                    : `Devoluciones activadas. La extensión ${cfg.extensionDevoluciones} no está en el teléfono del CRM: necesita la app de 3CX abierta`,
              );
            })
            .catch((e: Error) => toast(e.message, 'error'))
            .finally(() => void qc.invalidateQueries({ queryKey: ['puente-estado'] }));
        }}
      >
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={cfg.activo} onChange={(e) => poner('activo', e.target.checked)} />
          <b>Devolver automáticamente las llamadas perdidas</b>
        </label>
        <span style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={cfg.desdeColas} onChange={(e) => poner('desdeColas', e.target.checked)} />
            Perdidas en una cola
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={cfg.desdeExtensiones} onChange={(e) => poner('desdeExtensiones', e.target.checked)} />
            Perdidas en una extensión
          </label>
        </span>
        <span style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {num('esperarMin', 'Esperar', 'min antes de devolver', 0, 240)}
          {num('intentos', 'Intentos', '', 1, 5)}
          {num('entreIntentosMin', 'Entre intentos', 'min', 1, 480)}
          {num('timbrarSeg', 'Timbrar al cliente', 's', 15, 90)}
        </span>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          Extensión que origina la llamada
          <input value={cfg.extensionDevoluciones} onChange={(e) => poner('extensionDevoluciones', e.target.value)} placeholder="p. ej. 9999" style={{ width: 110 }} />
        </label>
        <p className="sub" style={{ margin: 0 }}>
          3CX saca la llamada desde una extensión que timbra primero. Usa una extensión dedicada (no la de una persona). Lo más fácil: configúrala en el
          teléfono del CRM (con su usuario y clave de 3CX) y el CRM la contesta solo, sin nada encendido. Si no, debe tener la app de 3CX abierta en un
          equipo siempre encendido.
        </p>
        <button className="btn sm primary">Guardar</button>
      </form>
    </section>
  );
}
